// Thin wrapper around workbox-window. VersionWatcher owns the "new version"
// UI; this module owns the worker lifecycle underneath it.
//
// The important subtlety: once a service worker controls the page, a plain
// window.location.reload() does NOT activate a waiting worker — the old shell
// is served straight back. The update has to be applied by telling the waiting
// worker to skipWaiting and reloading only after it takes control.

import { Workbox } from 'workbox-window';

let wb = null;
let updateReady = false;
const listeners = new Set();

const isLocalhost = () =>
  ['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname);

function markReady() {
  updateReady = true;
  listeners.forEach((fn) => fn());
}

export function register() {
  if (process.env.NODE_ENV !== 'production') return;
  if (!('serviceWorker' in navigator)) return;
  // Service workers require a secure context.
  if (window.location.protocol !== 'https:' && !isLocalhost()) return;

  window.addEventListener('load', () => {
    wb = new Workbox('/service-worker.js');

    // A worker is installed and parked behind the active one.
    wb.addEventListener('waiting', markReady);
    // Fires when a worker that was already waiting is found at registration time.
    wb.addEventListener('externalwaiting', markReady);

    wb.register().catch((err) => {
      // A failed registration must never take the app down with it.
      console.error('Service worker registration failed:', err);
    });
  });
}

/** Ask the browser to re-check for a new worker. Safe to call when unregistered. */
export function checkForUpdate() {
  if (wb) wb.update().catch(() => {});
}

/** True once a new worker is waiting to take over. */
export function isUpdateReady() {
  return updateReady;
}

export function onUpdateReady(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Activate the waiting worker and reload onto the new shell. Falls back to a
 * plain reload when no worker is involved (unsupported browser, dev build).
 */
export function applyUpdate() {
  if (!wb || !updateReady) {
    window.location.reload();
    return;
  }
  // Reload only after the new worker is in control, otherwise we'd just
  // re-render the old precached shell.
  wb.addEventListener('controlling', () => window.location.reload());
  wb.messageSkipWaiting();
}
